import { type ReactNode, Suspense, lazy } from 'react';

import { LandingPage } from '@/routes/index';

const AdminMfaGate = lazy(() =>
  import('@/components/admin-mfa-gate').then((module) => ({ default: module.AdminMfaGate })),
);
const AboutPage = lazy(() =>
  import('@/routes/about').then((module) => ({ default: module.AboutPage })),
);
const DashboardPage = lazy(() =>
  import('@/routes/dashboard').then((module) => ({ default: module.DashboardPage })),
);
const FAQPage = lazy(() => import('@/routes/faq').then((module) => ({ default: module.FAQPage })));
const HowItWorksPage = lazy(() =>
  import('@/routes/how-it-works').then((module) => ({ default: module.HowItWorksPage })),
);
const InvitePage = lazy(() =>
  import('@/routes/invite').then((module) => ({ default: module.InvitePage })),
);
const LegalPages = lazy(() =>
  import('@/routes/legal').then((module) => ({
    default: ({ page }: { page: 'cookie-policy' | 'privacy' | 'terms' }) => {
      if (page === 'cookie-policy') {
        return <module.CookiePolicyPage />;
      }

      if (page === 'terms') {
        return <module.TermsPage />;
      }

      return <module.PrivacyPage />;
    },
  })),
);
const LoginPage = lazy(() =>
  import('@/routes/login').then((module) => ({ default: module.LoginPage })),
);
const OptOutPage = lazy(() =>
  import('@/routes/opt-out').then((module) => ({ default: module.OptOutPage })),
);
const RewardsPage = lazy(() =>
  import('@/routes/rewards').then((module) => ({ default: module.RewardsPage })),
);
const VerifyPage = lazy(() =>
  import('@/routes/verify').then((module) => ({ default: module.VerifyPage })),
);
const AdminCashoutEconomicsPage = lazy(() =>
  import('@/routes/admin/cashout-economics').then((module) => ({
    default: module.AdminCashoutEconomicsPage,
  })),
);
const AdminCashoutHoldsPage = lazy(() =>
  import('@/routes/admin/cashout-holds').then((module) => ({
    default: module.AdminCashoutHoldsPage,
  })),
);
const AdminPasswordResetPage = lazy(() =>
  import('@/routes/admin/reset-password').then((module) => ({
    default: module.AdminPasswordResetPage,
  })),
);
const ClientSpeederFilesPage = lazy(() =>
  import('@/routes/admin/client-speeder-files').then((module) => ({
    default: module.ClientSpeederFilesPage,
  })),
);
const CronStatusPage = lazy(() =>
  import('@/routes/admin/cron-status').then((module) => ({ default: module.CronStatusPage })),
);
const AdminDisqualificationCreditsPage = lazy(() =>
  import('@/routes/admin/disqualification-credits').then((module) => ({
    default: module.AdminDisqualificationCreditsPage,
  })),
);
const FunnelPage = lazy(() =>
  import('@/routes/admin/funnel').then((module) => ({ default: module.FunnelPage })),
);
const AdminFeasibilityPage = lazy(() =>
  import('@/routes/admin/feasibility').then((module) => ({
    default: module.AdminFeasibilityPage,
  })),
);
const AdminPanelistsPage = lazy(() =>
  import('@/routes/admin/panelists').then((module) => ({ default: module.AdminPanelistsPage })),
);
const ReconciliationAlertsPage = lazy(() =>
  import('@/routes/admin/reconciliation-alerts').then((module) => ({
    default: module.ReconciliationAlertsPage,
  })),
);
const AdminRedemptionsPage = lazy(() =>
  import('@/routes/admin/redemptions').then((module) => ({ default: module.AdminRedemptionsPage })),
);
const AdminProfileSecReviewsPage = lazy(() =>
  import('@/routes/admin/sec-reviews').then((module) => ({
    default: module.AdminProfileSecReviewsPage,
  })),
);
const SourceCountsPage = lazy(() =>
  import('@/routes/admin/source-counts').then((module) => ({ default: module.SourceCountsPage })),
);

const AdminStudiesPage = lazy(() =>
  import('@/routes/admin/studies').then((module) => ({ default: module.AdminStudiesPage })),
);
const AdminStudyEditorPage = lazy(() =>
  import('@/routes/admin/studies-edit').then((module) => ({
    default: module.AdminStudyEditorPage,
  })),
);
const AdminStudyPreviewPage = lazy(() =>
  import('@/routes/admin/studies-preview').then((module) => ({
    default: module.AdminStudyPreviewPage,
  })),
);
const AdminStudyInvitationsPage = lazy(() =>
  import('@/routes/admin/study-invitations').then((module) => ({
    default: module.AdminStudyInvitationsPage,
  })),
);
const AdminStudyFieldingMonitorPage = lazy(() =>
  import('@/routes/admin/study-fielding-monitor').then((module) => ({
    default: module.AdminStudyFieldingMonitorPage,
  })),
);
const AdminStudyQuotasPage = lazy(() =>
  import('@/routes/admin/study-quotas').then((module) => ({
    default: module.AdminStudyQuotasPage,
  })),
);
const StudyPage = lazy(() =>
  import('@/routes/study').then((module) => ({ default: module.StudyPage })),
);
const ShortSurveyLinkPage = lazy(() =>
  import('@/routes/short-survey-link').then((module) => ({
    default: module.ShortSurveyLinkPage,
  })),
);

function lazyRoute(element: ReactNode) {
  return (
    <Suspense
      fallback={
        <main className="min-h-screen bg-background p-6 font-mono text-sm font-bold uppercase text-muted-foreground">
          Loading...
        </main>
      }
    >
      {element}
    </Suspense>
  );
}

function adminRoute(element: ReactNode) {
  return lazyRoute(<AdminMfaGate>{element}</AdminMfaGate>);
}

export function getAppRoute(
  pathname: string,
):
  | 'about'
  | 'cron-status'
  | 'dashboard'
  | 'faq'
  | 'how-it-works'
  | 'client-speeder-files'
  | 'cashout-economics'
  | 'cashout-holds'
  | 'cookie-policy'
  | 'disqualification-credits'
  | 'feasibility'
  | 'funnel'
  | 'invite'
  | 'landing'
  | 'login'
  | 'opt-out'
  | 'panelists'
  | 'privacy'
  | 'rewards'
  | 'redemptions'
  | 'reset-password'
  | 'reconciliation-alerts'
  | 'source-counts'
  | 'sec-reviews'
  | 'short-survey-link'
  | 'studies'
  | 'study-editor'
  | 'study-fielding'
  | 'study-fielding-monitor'
  | 'study-invitations'
  | 'study-preview'
  | 'study-quotas'
  | 'terms'
  | 'verify'
  | 'your-points' {
  if (pathname === '/about') {
    return 'about';
  }

  if (pathname === '/admin/panelists' || pathname.startsWith('/admin/panelists/')) {
    return 'panelists';
  }

  if (pathname === '/admin/cron-status') {
    return 'cron-status';
  }

  if (pathname === '/admin/cashout-economics') {
    return 'cashout-economics';
  }

  if (pathname === '/admin/cashout-holds') {
    return 'cashout-holds';
  }

  if (pathname === '/admin/reset-password') {
    return 'reset-password';
  }

  if (pathname === '/admin/disqualification-credits') {
    return 'disqualification-credits';
  }

  if (pathname === '/admin/redemptions') {
    return 'redemptions';
  }

  if (pathname === '/admin/client-speeder-files') {
    return 'client-speeder-files';
  }

  if (pathname === '/admin/studies' || pathname === '/admin/studies/') {
    return 'studies';
  }

  if (/^\/admin\/studies\/[^/]+\/edit$/.test(pathname)) {
    return 'study-editor';
  }

  if (/^\/admin\/studies\/[^/]+\/preview$/.test(pathname)) {
    return 'study-preview';
  }

  if (/^\/admin\/studies\/[^/]+\/invite$/.test(pathname)) {
    return 'study-invitations';
  }

  if (/^\/admin\/studies\/[^/]+\/quotas$/.test(pathname)) {
    return 'study-quotas';
  }

  if (/^\/admin\/studies\/[^/]+\/fielding$/.test(pathname)) {
    return 'study-fielding-monitor';
  }

  if (/^\/study\/[^/]+\/[^/]+$/.test(pathname)) {
    return 'study-fielding';
  }

  if (/^\/s\/[^/]+$/.test(pathname)) {
    return 'short-survey-link';
  }

  if (pathname === '/admin/funnel') {
    return 'funnel';
  }

  if (pathname === '/admin/feasibility') {
    return 'feasibility';
  }

  if (pathname === '/admin/reconciliation-alerts') {
    return 'reconciliation-alerts';
  }

  if (pathname === '/admin/source-counts') {
    return 'source-counts';
  }

  if (pathname === '/admin/profile/sec-reviews') {
    return 'sec-reviews';
  }

  if (pathname === '/faq') {
    return 'faq';
  }

  if (pathname === '/how-it-works') {
    return 'how-it-works';
  }

  if (pathname === '/invite') {
    return 'invite';
  }

  if (pathname === '/login') {
    return 'login';
  }

  if (pathname === '/opt-out') {
    return 'opt-out';
  }

  if (pathname === '/cookie-policy') {
    return 'cookie-policy';
  }

  if (pathname === '/privacy') {
    return 'privacy';
  }

  if (pathname === '/rewards') {
    return 'rewards';
  }

  if (pathname === '/terms') {
    return 'terms';
  }

  if (pathname === '/verify') {
    return 'verify';
  }

  if (pathname === '/welcome' || pathname === '/dashboard' || pathname === '/your-points') {
    return 'dashboard';
  }

  return 'landing';
}

export default function App() {
  const route = getAppRoute(typeof window === 'undefined' ? '/' : window.location.pathname);

  if (route === 'about') {
    return lazyRoute(<AboutPage />);
  }

  if (route === 'cron-status') {
    return adminRoute(lazyRoute(<CronStatusPage />));
  }

  if (route === 'cashout-economics') {
    return adminRoute(lazyRoute(<AdminCashoutEconomicsPage />));
  }

  if (route === 'cashout-holds') {
    return adminRoute(lazyRoute(<AdminCashoutHoldsPage />));
  }

  if (route === 'reset-password') {
    return lazyRoute(<AdminPasswordResetPage />);
  }

  if (route === 'disqualification-credits') {
    return adminRoute(lazyRoute(<AdminDisqualificationCreditsPage />));
  }

  if (route === 'panelists') {
    return adminRoute(lazyRoute(<AdminPanelistsPage />));
  }

  if (route === 'redemptions') {
    return adminRoute(lazyRoute(<AdminRedemptionsPage />));
  }

  if (route === 'client-speeder-files') {
    return adminRoute(lazyRoute(<ClientSpeederFilesPage />));
  }

  if (route === 'studies') {
    return adminRoute(lazyRoute(<AdminStudiesPage />));
  }

  if (route === 'study-editor') {
    return adminRoute(lazyRoute(<AdminStudyEditorPage />));
  }

  if (route === 'study-preview') {
    return adminRoute(lazyRoute(<AdminStudyPreviewPage />));
  }

  if (route === 'study-invitations') {
    return adminRoute(lazyRoute(<AdminStudyInvitationsPage />));
  }

  if (route === 'study-quotas') {
    return adminRoute(lazyRoute(<AdminStudyQuotasPage />));
  }

  if (route === 'study-fielding-monitor') {
    return adminRoute(lazyRoute(<AdminStudyFieldingMonitorPage />));
  }

  if (route === 'study-fielding') {
    return lazyRoute(<StudyPage />);
  }

  if (route === 'short-survey-link') {
    return lazyRoute(<ShortSurveyLinkPage />);
  }

  if (route === 'source-counts') {
    return adminRoute(lazyRoute(<SourceCountsPage />));
  }

  if (route === 'reconciliation-alerts') {
    return adminRoute(lazyRoute(<ReconciliationAlertsPage />));
  }

  if (route === 'sec-reviews') {
    return adminRoute(lazyRoute(<AdminProfileSecReviewsPage />));
  }

  if (route === 'funnel') {
    return adminRoute(lazyRoute(<FunnelPage />));
  }

  if (route === 'feasibility') {
    return adminRoute(lazyRoute(<AdminFeasibilityPage />));
  }

  if (route === 'invite') {
    return lazyRoute(<InvitePage />);
  }

  if (route === 'faq') {
    return lazyRoute(<FAQPage />);
  }

  if (route === 'how-it-works') {
    return lazyRoute(<HowItWorksPage />);
  }

  if (route === 'login') {
    return lazyRoute(<LoginPage />);
  }

  if (route === 'opt-out') {
    return lazyRoute(<OptOutPage />);
  }

  if (route === 'cookie-policy') {
    return lazyRoute(<LegalPages page="cookie-policy" />);
  }

  if (route === 'privacy') {
    return lazyRoute(<LegalPages page="privacy" />);
  }

  if (route === 'rewards') {
    return lazyRoute(<RewardsPage />);
  }

  if (route === 'terms') {
    return lazyRoute(<LegalPages page="terms" />);
  }

  if (route === 'verify') {
    return lazyRoute(<VerifyPage />);
  }

  if (route === 'dashboard') {
    return lazyRoute(<DashboardPage />);
  }

  return <LandingPage />;
}
